/**
 * レイアウト計算（時間軸 → %、列幅、バーの段組み、印刷の列幅）
 */
import { hmToMinutes } from './time';

export interface TimeAxis {
  /** 軸の開始（分。例 6:00 = 360） */
  startMinutes: number;
  /** 軸の長さ（分） */
  totalMinutes: number;
  /** 目盛りの刻み（分） */
  slotMinutes: number;
}

export const buildAxis = (dispStartHour: number, dispHour: number, slotMinutes: number): TimeAxis => ({
  startMinutes: dispStartHour * 60,
  totalMinutes: Math.max(60, dispHour * 60),
  slotMinutes,
});

/** 分 → 軸上の % */
export const minutesToPercent = (axis: TimeAxis, minutes: number): number => {
  const p = ((minutes - axis.startMinutes) / axis.totalMinutes) * 100;
  return Math.max(0, Math.min(100, p));
};

/** % → 分（刻みに丸める） */
export const percentToMinutes = (axis: TimeAxis, percent: number): number => {
  const raw = axis.startMinutes + (percent / 100) * axis.totalMinutes;
  return Math.round(raw / axis.slotMinutes) * axis.slotMinutes;
};

/** "HH:mm" 開始・終了 → 軸上の left/width（%）。翌日跨ぎは +1440 */
export const barGeometry = (
  axis: TimeAxis, start: string, end: string, isEndNextDay: boolean,
): { left: number; width: number } => {
  let s = hmToMinutes(start);
  let e = hmToMinutes(end, isEndNextDay);
  if (e <= s) {
    e += 1440;
  }
  // 軸の開始より前に始まる深夜帯は翌日側に置く
  if (s < axis.startMinutes) {
    s += 1440;
    e += 1440;
  }
  const left = minutesToPercent(axis, s);
  const right = minutesToPercent(axis, e);
  return { left, width: Math.max(0.5, right - left) };
};

/** 軸の目盛り（時間ごと） */
export const axisTicks = (axis: TimeAxis): Array<{ label: string; percent: number; major: boolean }> => {
  const ticks: Array<{ label: string; percent: number; major: boolean }> = [];
  for (let m = axis.startMinutes; m <= axis.startMinutes + axis.totalMinutes; m += 60) {
    const h = Math.floor(m / 60) % 24;
    ticks.push({ label: `${h}`, percent: minutesToPercent(axis, m), major: h % 6 === 0 });
  }
  return ticks;
};

/**
 * 同じ行に重なるバーを段に振り分ける（重なりが無ければ 0 段目）
 * @returns 各バーの段番号と段数
 */
export const assignLanes = (spans: Array<[number, number]>): { lanes: Array<number>; laneCount: number } => {
  const order = spans.map((s, i) => i).sort((a, b) => spans[a][0] - spans[b][0]);
  const laneEnds: Array<number> = [];
  const lanes: Array<number> = new Array(spans.length).fill(0);
  order.forEach((i) => {
    const [s, e] = spans[i];
    let lane = laneEnds.findIndex((end) => end <= s);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(e);
    } else {
      laneEnds[lane] = e;
    }
    lanes[i] = lane;
  });
  return { lanes, laneCount: Math.max(1, laneEnds.length) };
};

/** ビューごとの列幅（px） */
export const columnWidth = (view: 'day' | 'week' | 'half' | 'month', isTablet: boolean): number => {
  switch (view) {
    case 'week':
      return isTablet ? 120 : 140;
    case 'half':
      return isTablet ? 76 : 88;
    case 'month':
      return isTablet ? 56 : 64;
    default:
      return 0;
  }
};

export const STAFF_COLUMN_WIDTH = 176;
export const TOTAL_COLUMN_WIDTH = 96;

/**
 * 印刷時: 用紙幅に合わせた列幅（mm）
 *
 * 下限は設けない（10/5）。表を必ず紙の幅に収める（改ページはしない）。日数が多いと列が細くなり、
 * 時刻はセルの中で切れて読めなくなる（以前は 6mm の下限で、A4 横の 38 日以上は表が紙の幅を超えて右側が切れていた）
 */
export const printColumnWidthMm = (
  dayCount: number, paper: 'A4' | 'A3', staffColumnMm: number = 34, totalColumnMm: number = 18, marginMm: number = 10,
  orientation: 'landscape' | 'portrait' = 'landscape',
): number => {
  const longSide = paper === 'A3' ? 420 : 297;
  const shortSide = paper === 'A3' ? 297 : 210;
  const pageWidth = orientation === 'portrait' ? shortSide : longSide;
  const usable = pageWidth - marginMm * 2 - staffColumnMm - totalColumnMm;
  return Math.max(0.1, Math.floor((usable / Math.max(1, dayCount)) * 10) / 10);
};
